import { UserCheck, MessageSquareText, Compass, Handshake } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal, SectionHeading, Beam } from "@/components/Reveal";
import { GraffitiTag } from "@/components/GraffitiTag";

const BG =
  "https://images.pexels.com/photos/11053643/pexels-photo-11053643.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const FEATURES = [
  {
    icon: UserCheck,
    title: "Personalized Dispatching",
    desc: "Solutions tailored to your equipment, preferences, and operational needs.",
  },
  {
    icon: MessageSquareText,
    title: "Clear Communication",
    desc: "Professional, consistent updates that help you stay informed.",
  },
  {
    icon: Compass,
    title: "Strategic Planning",
    desc: "Thoughtful load coordination focused on supporting your business objectives.",
  },
  {
    icon: Handshake,
    title: "Carrier-Focused Service",
    desc: "We prioritize relationships and long-term success, not simply booking the next load.",
  },
];

export const WhyChoose = () => (
  <section id="why-us" data-testid="why-choose-section" className="relative overflow-hidden py-20 lg:py-32 bg-surface border-y border-gold/10">
    <GraffitiTag watermark rotate={-6} color="text-champagne" className="absolute -right-8 bottom-6 text-[24vw] lg:text-[12rem] leading-none pointer-events-none select-none hidden md:block">
      Precision
    </GraffitiTag>
    <Beam />
    <div className="relative px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
      <Reveal className="relative order-2 lg:order-1">
        <motion.div
          className="absolute -inset-3 border border-gold/60 pointer-events-none"
          initial={{ opacity: 0, scale: 1.04 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
        />
        <motion.div
          className="absolute -inset-3 pointer-events-none"
          animate={{ boxShadow: ["0 0 0px rgba(212,175,55,0)", "0 0 45px rgba(212,175,55,0.25)", "0 0 0px rgba(212,175,55,0)"] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        />
        <div className="relative overflow-hidden">
          <img
            src={BG}
            alt="Cargo truck traveling a highway beneath a twilight mountain sky"
            loading="lazy"
            className="w-full aspect-[4/5] sm:aspect-[5/4] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-ink/20" />
        </div>
      </Reveal>

      <div className="order-1 lg:order-2">
        <SectionHeading
          overline="Why Choose B&H Truck Dispatching?"
          title="Your Business Deserves More Than Just Another Dispatcher."
          testId="why-choose-heading"
        />
        <Reveal delay={0.1}>
          <p className="text-zinc-400 text-sm sm:text-base leading-relaxed mb-10">
            We know that your truck is more than equipment. It's your livelihood, your investment, and your business.
            That's why we approach every carrier relationship with professionalism, dedication, and attention to detail.
          </p>
        </Reveal>
        <div className="space-y-6">
          {FEATURES.map((f, i) => (
            <Reveal key={f.title} delay={0.1 + 0.08 * i}>
              <div data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`} className="flex gap-5 group">
                <div className="w-11 h-11 shrink-0 flex items-center justify-center border border-gold/40 bg-gold/5 group-hover:bg-gold/15 transition-colors duration-300">
                  <f.icon size={20} className="text-gold" />
                </div>
                <div>
                  <h3 className="font-display text-lg uppercase tracking-wide text-white mb-1">{f.title}</h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">{f.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);
